/**
 * Finding and marking empty mandatory fields.
 *
 * The rules mirror the ones the main form applies when it is saved (see
 * SemDynamicComponent): a field counts as mandatory when it carries `required` or
 * `data-semrequired` (the latter is set by SemRequiredDirective, which also honours the
 * conditional many-to-one case), hidden fields are skipped, and the marking class is the
 * same `wg-field-invalid` the stylesheet already knows.
 *
 * It lives here because a one-to-many section can be rendered in a dialog, outside the
 * main form element - the form's own scan is rooted at the form and cannot see it.
 */

const INVALID_CLASS = "wg-field-invalid";

function isEmptyControl(el: Element): boolean {
  const tag = el.tagName.toLowerCase();

  if (tag === "input" || tag === "textarea" || tag === "select") {
    const input = el as HTMLInputElement;
    if (input.type === "checkbox") {
      return !input.checked;
    }
    return !input.value || input.value.trim().length === 0;
  }

  if (tag === "sem-check-box") {
    const checkbox = el.querySelector('input[type="checkbox"]') as HTMLInputElement;
    return checkbox ? !checkbox.checked : false;
  }

  if (tag.startsWith("sem-") || tag.startsWith("mat-")) {
    const inner = el.querySelector("input, textarea, select") as HTMLInputElement;
    if (inner) {
      return !inner.value || inner.value.trim().length === 0;
    }

    // dropdowns render the selected value as text, not as an input
    const selected = el.querySelector(".mat-mdc-select-value, .ng-select-container");
    if (selected) {
      return !selected.textContent || selected.textContent.trim().length === 0;
    }
  }

  return false;
}

/**
 * Returns the visible mandatory fields inside `root` that have been left empty.
 */
export function findEmptyRequiredFields(root: ParentNode): HTMLElement[] {
  const empty: HTMLElement[] = [];

  root.querySelectorAll("[required], [data-semrequired]").forEach((el: Element) => {
    // a field the user cannot see is not a field the user can fill in
    if ((el as HTMLElement).offsetParent === null) {
      return;
    }

    if (isEmptyControl(el)) {
      empty.push(el as HTMLElement);
    }
  });

  return empty;
}

/**
 * Marks the fields as invalid and clears the marking again as soon as the user
 * types in one of them.
 */
export function markFieldsInvalid(fields: HTMLElement[], onCleared?: () => void): void {
  fields.forEach(el => {
    el.classList.add(INVALID_CLASS);

    const clear = () => {
      el.classList.remove(INVALID_CLASS);
      el.classList.remove("is-invalid");
      el.querySelectorAll("." + INVALID_CLASS).forEach((c: Element) => c.classList.remove(INVALID_CLASS));
      if (onCleared) {
        onCleared();
      }
    };

    const tag = el.tagName.toLowerCase();
    const isCheckbox = tag === "sem-check-box" || (tag === "input" && (el as HTMLInputElement).type === "checkbox");
    const event = isCheckbox ? "change" : "input";

    if (tag.startsWith("sem-") || tag.startsWith("mat-")) {
      el.querySelectorAll("input, textarea, .mdc-text-field").forEach((inner: Element) => {
        inner.classList.add(INVALID_CLASS);
        inner.addEventListener(event, clear, { once: true });
      });
      // dropdowns emit neither input nor change on the host - catch the selection click
      el.addEventListener("click", clear, { once: true });
    } else {
      el.addEventListener(event, clear, { once: true });
    }
  });
}

/**
 * Removes every invalid marking below `root`.
 */
export function clearInvalidMarkings(root: ParentNode): void {
  root.querySelectorAll("." + INVALID_CLASS).forEach((el: Element) => {
    el.classList.remove(INVALID_CLASS);
    el.classList.remove("is-invalid");
  });
}

/**
 * True when `el` is one of the page-level copies of the section controls of a one-to-many
 * container that edits its sections in a dialog.
 *
 * The page template carries the section controls of a container outside the container
 * element: once in the hidden template right after it (`<div #contentTemplate class="o2m-content">`)
 * and once more after its end marker (`<span class="<binding>">`). Those copies are never shown,
 * but their ngModel registers in the page form and binds to the page model, so a mandatory
 * section field there is always empty. Section controls carry the `o2m-content` class; page
 * controls do not. In the dialog mode the dialog checks the section on confirm, so these
 * copies must not block the page save.
 */
export function isInDialogSectionTemplate(el: Element): boolean {
  if (!el.closest(".o2m-content") || el.closest("sem-one-to-many-container")) {
    return false;
  }

  let node: Element | null = el;

  while (node) {
    for (let s = node.previousElementSibling; s; s = s.previousElementSibling) {
      if (s.tagName.toLowerCase() === "sem-one-to-many-container") {
        // the nearest container before the copy is the one it belongs to
        return s.getAttribute("data-edit-in-dialog") === "true";
      }
    }
    node = node.parentElement;
  }

  return false;
}

/**
 * Names of the invalid controls of `form` that are NOT explained by a dialog section
 * template. An empty result means the form is invalid only because of those hidden copies.
 * A name with no element in `formEl` counts as a real error (nothing proves it is a copy).
 */
export function invalidControlsOutsideDialogSections(
  controls: { [name: string]: { invalid: boolean } },
  formEl: ParentNode,
): string[] {
  const real: string[] = [];

  for (const name in controls) {
    if (!controls[name] || !controls[name].invalid) {
      continue;
    }

    const esc = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(name) : name.replace(/"/g, '\\"');
    const els = Array.from(formEl.querySelectorAll('[name="' + esc + '"], [name="model.' + esc + '"]'));

    if (els.length === 0 || els.some(e => !isInDialogSectionTemplate(e))) {
      real.push(name);
    }
  }

  return real;
}
